import React, { useState, useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import Navbar from "../components/Navbar";
import Footer from "../components/Footer";
import { LOGO_URL } from "../mock/mock";
import {
  ArrowRight,
  MailOpen,
  Compass,
  Sparkles,
  Zap,
  Globe2,
  Lightbulb,
  BadgeCheck,
} from "lucide-react";

/* ============ Shared reveal ============ */
const useReveal = (options = {}) => {
  const ref = useRef(null);
  const [inView, setInView] = useState(false);
  useEffect(() => {
    const node = ref.current;
    if (!node) return;
    if (typeof window !== "undefined" && window.matchMedia("(prefers-reduced-motion: reduce)").matches) {
      setInView(true);
      return;
    }
    const obs = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setInView(true);
          obs.unobserve(node);
        }
      },
      { threshold: 0.2, rootMargin: "0px 0px -60px 0px", ...options }
    );
    obs.observe(node);
    return () => obs.disconnect();
  }, []);
  return { ref, inView };
};

/* ============ Hero ============ */
const Hero = () => {
  const { ref, inView } = useReveal();
  const style = {
    transform: inView ? "translate3d(0,0,0)" : "translate3d(0, 28px, 0)",
    opacity: inView ? 1 : 0,
    transition:
      "transform 900ms cubic-bezier(0.22, 1, 0.36, 1), opacity 700ms cubic-bezier(0.22, 1, 0.36, 1)",
    willChange: "transform, opacity",
  };
  return (
    <section className="relative overflow-hidden" data-testid="story-hero-section">
      <div className="absolute inset-0 -z-10">
        <div className="absolute -top-40 -left-40 w-[720px] h-[720px] bg-[#bcd3ff] rounded-full blur-3xl opacity-90" />
        <div className="absolute -top-20 right-[-160px] w-[640px] h-[640px] bg-[#d5e4ff] rounded-full blur-3xl opacity-95" />
        <div className="absolute inset-0 bg-[radial-gradient(circle_at_top,rgba(255,255,255,0.7),transparent_65%)]" />
      </div>
      <div ref={ref} style={style} className="max-w-4xl mx-auto px-6 pt-24 md:pt-32 pb-14 text-center">
        <span className="inline-block px-3 py-1 rounded-full bg-white/70 backdrop-blur border border-[#dbe6ff] text-[#1d4ed8] text-[12px] font-semibold tracking-[0.14em] uppercase shadow-sm">
          Our Story
        </span>
        <h1
          className="mt-6 font-semibold text-slate-900 leading-[1.02] tracking-tight text-[46px] md:text-[72px]"
          data-testid="story-heading"
        >
          Why we built{" "}
          <span className="relative inline-block">
            <span className="relative z-10 text-[#1d4ed8]">Medily</span>
            <span className="absolute left-0 right-0 bottom-1 h-3 bg-[#dbe6ff]/70 -z-0 rounded-sm" />
          </span>
          .
        </h1>
        <p className="mt-7 text-slate-500 text-[17px] md:text-[19px] leading-relaxed max-w-2xl mx-auto">
          Preparing for medical school interviews can feel overwhelming. We created Medily to make
          MMI preparation simpler, more accessible and built around the needs of today’s applicants.
        </p>
      </div>
    </section>
  );
};

/* ============ Interactive Letter ============ */
const LETTER_PARAGRAPHS = [
  "Dear aspiring medic,",
  "If you’re reading this, you’re probably somewhere in the middle of preparing for your medical school interviews. It’s an exciting time, but it can also be confusing, stressful and, at times, a little overwhelming.",
  "When we were preparing ourselves, one of the hardest parts wasn’t the interviews—it was knowing where to start. There were endless notes, conflicting advice and so many different opinions about what actually mattered. It often felt like you needed to spend hours sorting through information before you could even begin preparing.",
  "That’s what inspired us to build Medily.",
  "We wanted to create something that made preparation feel simpler. Instead of giving students hundreds of pages to memorise, we wanted to focus on the concepts, skills and practice that genuinely help you become a better interview candidate. We also wanted everything to stay relevant, with resources that keep up with changing healthcare topics and the way interviews continue to evolve.",
  "We also believe that preparing for medicine shouldn’t depend on how much you can afford or where you live. Every aspiring medic should have access to high-quality preparation, realistic practice and the latest technology that can help them improve with confidence.",
  "Whether you’re reading one of our guides, practising with Maya or joining one of our mock interviews, we hope Medily helps you feel a little more prepared every time you use it.",
  "Most importantly, we hope it reminds you that interviews aren’t about having the perfect answer. They’re about showing the person behind the application—the qualities that made you choose medicine in the first place.",
  "Thank you for letting us be part of your journey. We wish you the very best with your interviews, and we hope to see you achieve the offer you’ve been working so hard for.",
];

const InteractiveLetter = () => {
  const { ref, inView } = useReveal();
  const [open, setOpen] = useState(false);
  const wrapStyle = {
    transform: inView ? "translate3d(0,0,0)" : "translate3d(0, 32px, 0)",
    opacity: inView ? 1 : 0,
    transition:
      "transform 900ms cubic-bezier(0.22, 1, 0.36, 1), opacity 700ms cubic-bezier(0.22, 1, 0.36, 1)",
    willChange: "transform, opacity",
  };

  return (
    <section
      ref={ref}
      className="relative py-16 md:py-24"
      style={wrapStyle}
      data-testid="story-letter-section"
    >
      <div className="absolute inset-0 -z-10 pointer-events-none">
        <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[720px] h-[720px] bg-[#dbe6ff]/60 rounded-full blur-3xl" />
      </div>

      <div className="max-w-3xl mx-auto px-6">
        {/* Envelope + Letter stage */}
        <div className="relative mx-auto" style={{ perspective: "1600px" }}>
          {/* ============ Envelope (front + back + flap) ============ */}
          <div
            className={`relative mx-auto w-full max-w-[560px] aspect-[16/10] transition-[transform,opacity] duration-[900ms] ease-[cubic-bezier(0.22,1,0.36,1)] ${
              open ? "translate-y-4 md:translate-y-8" : ""
            }`}
            data-testid="story-envelope"
            aria-hidden={open}
          >
            {/* Ambient glow */}
            <div className="absolute -inset-8 -z-10 rounded-[40px] bg-gradient-to-br from-[#c9dcff] via-[#dbe6ff] to-transparent blur-3xl opacity-90" />

            {/* Body (front face) — contains the visible pocket + shadow */}
            <div className="absolute inset-0 rounded-[18px] bg-white border border-slate-100 shadow-[0_45px_100px_-25px_rgba(29,78,216,0.5)] overflow-hidden">
              {/* Inner pocket */}
              <div className="absolute inset-x-6 top-6 bottom-6 rounded-[12px] bg-gradient-to-b from-[#f7faff] to-white border border-[#eef4ff]" />
              {/* Wax seal */}
              {!open && (
                <div className="absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 flex flex-col items-center gap-3">
                  <span className="w-14 h-14 rounded-full bg-gradient-to-br from-[#1d4ed8] to-[#3b6ff5] text-white flex items-center justify-center shadow-[0_15px_35px_-10px_rgba(29,78,216,0.7)]">
                    <MailOpen className="w-6 h-6" />
                  </span>
                  <span className="text-[11px] font-semibold tracking-[0.24em] uppercase text-slate-500">
                    A letter from the Medily Team
                  </span>
                </div>
              )}
            </div>

            {/* Flap (top triangle) — rotates around top edge */}
            <div
              className="absolute inset-x-0 top-0 origin-top"
              style={{
                height: "55%",
                transform: open ? "rotateX(-180deg)" : "rotateX(0deg)",
                transition: "transform 1100ms cubic-bezier(0.22, 1, 0.36, 1)",
                transformStyle: "preserve-3d",
                willChange: "transform",
              }}
              data-testid="story-envelope-flap"
            >
              <div
                className="w-full h-full bg-white border border-slate-100"
                style={{
                  clipPath: "polygon(0 0, 100% 0, 50% 100%)",
                  boxShadow: open
                    ? "0 -20px 40px -20px rgba(29,78,216,0.25)"
                    : "0 15px 30px -15px rgba(29,78,216,0.35)",
                }}
              />
              {/* Back-side of flap when open (subtle blue) */}
              <div
                className="absolute inset-0"
                style={{
                  transform: "rotateX(180deg)",
                  backfaceVisibility: "hidden",
                  clipPath: "polygon(0 0, 100% 0, 50% 100%)",
                  background: "linear-gradient(180deg, #eef4ff 0%, #ffffff 100%)",
                  border: "1px solid #eef4ff",
                }}
              />
            </div>

            {/* Open Letter button — sits below the sealed envelope */}
            {!open && (
              <button
                type="button"
                onClick={() => setOpen(true)}
                className="absolute left-1/2 -translate-x-1/2 -bottom-14 md:-bottom-16 inline-flex items-center gap-2 px-7 py-3.5 rounded-full bg-[#1d4ed8] text-white font-medium hover:bg-[#1e40af] transition shadow-[0_18px_40px_-12px_rgba(29,78,216,0.65)]"
                data-testid="story-open-letter-btn"
              >
                Open Letter <ArrowRight className="w-4 h-4" />
              </button>
            )}
          </div>

          {/* ============ The letter itself — slides up from behind the envelope ============ */}
          <div
            className="relative mx-auto max-w-[640px] mt-8 md:mt-14"
            style={{
              transform: open ? "translate3d(0, 0, 0)" : "translate3d(0, -180px, 0)",
              opacity: open ? 1 : 0,
              transition:
                "transform 1400ms cubic-bezier(0.22, 1, 0.36, 1) 250ms, opacity 900ms cubic-bezier(0.22, 1, 0.36, 1) 350ms",
              pointerEvents: open ? "auto" : "none",
              willChange: "transform, opacity",
            }}
            data-testid="story-letter"
            aria-hidden={!open}
          >
            <div className="rounded-[24px] bg-white border border-slate-100 shadow-[0_45px_120px_-25px_rgba(29,78,216,0.5)] p-8 md:p-12">
              {/* subtle ruled corner accent — this now acts as the letter's section heading */}
              <div className="flex items-center gap-3">
                <span className="w-8 h-px bg-[#1d4ed8]/40" />
                <span className="text-[20px] md:text-[26px] font-semibold tracking-tight text-[#1d4ed8]" data-testid="story-letter-heading">
                  A letter from the Medily team
                </span>
              </div>

              <div className="mt-6 space-y-5 text-slate-700 leading-relaxed text-[15.5px]">
                {LETTER_PARAGRAPHS.map((p, i) => (
                  <p key={i} className={i === 0 ? "text-slate-900 font-medium" : ""}>{p}</p>
                ))}
              </div>

              <div className="mt-10 pt-6 border-t border-slate-100 flex flex-col items-center gap-3">
                <p className="text-slate-500 italic text-[14px]">— The Medily Team</p>
                <img
                  src={LOGO_URL}
                  alt="Medily"
                  className="h-10 md:h-12 opacity-90"
                  data-testid="story-letter-signature-logo"
                  draggable="false"
                />
              </div>
            </div>

            {open && (
              <div className="mt-6 text-center">
                <button
                  type="button"
                  onClick={() => setOpen(false)}
                  className="text-[13px] text-slate-500 hover:text-[#1d4ed8] transition underline underline-offset-4"
                  data-testid="story-close-letter-btn"
                >
                  Fold letter back
                </button>
              </div>
            )}
          </div>
        </div>
      </div>
    </section>
  );
};

/* ============ Mission ============ */
const MISSION_CARDS = [
  {
    id: "accessible",
    icon: Globe2,
    title: "Accessible",
    body:
      "Making high-quality MMI preparation available to aspiring medics, wherever they are.",
  },
  {
    id: "up-to-date",
    icon: Sparkles,
    title: "Up to Date",
    body:
      "Resources are continually reviewed and improved to reflect current interview expectations and evolving healthcare topics.",
  },
  {
    id: "future",
    icon: Zap,
    title: "Built for the Future",
    body:
      "Combining expert knowledge with modern AI technology to help students prepare smarter, practise realistically and stand out.",
  },
];

const MissionCard = ({ item, i }) => {
  const { ref, inView } = useReveal();
  const Icon = item.icon;
  const style = {
    transform: inView ? "translate3d(0,0,0)" : "translate3d(0, 24px, 0)",
    opacity: inView ? 1 : 0,
    transition: `transform 900ms cubic-bezier(0.22, 1, 0.36, 1) ${i * 120}ms, opacity 700ms cubic-bezier(0.22, 1, 0.36, 1) ${i * 120}ms`,
    willChange: "transform, opacity",
  };
  return (
    <div
      ref={ref}
      style={style}
      className="rounded-3xl bg-white border border-slate-100 p-7 md:p-8 shadow-[0_25px_60px_-25px_rgba(29,78,216,0.35)] hover:shadow-[0_35px_75px_-25px_rgba(29,78,216,0.5)] hover:-translate-y-1 transition-all duration-500"
      data-testid={`mission-card-${item.id}`}
    >
      <span className="inline-flex w-11 h-11 rounded-2xl bg-[#eaf1ff] text-[#1d4ed8] items-center justify-center">
        <Icon className="w-[18px] h-[18px]" />
      </span>
      <h3 className="mt-5 text-[20px] font-semibold text-slate-900 tracking-tight">{item.title}</h3>
      <p className="mt-2 text-[14.5px] text-slate-600 leading-relaxed">{item.body}</p>
    </div>
  );
};

const Mission = () => {
  const { ref, inView } = useReveal();
  const style = {
    transform: inView ? "translate3d(0,0,0)" : "translate3d(0, 28px, 0)",
    opacity: inView ? 1 : 0,
    transition:
      "transform 900ms cubic-bezier(0.22, 1, 0.36, 1), opacity 700ms cubic-bezier(0.22, 1, 0.36, 1)",
    willChange: "transform, opacity",
  };
  return (
    <section
      className="max-w-6xl mx-auto px-6 pt-24 md:pt-32 pb-16"
      data-testid="story-mission-section"
    >
      <div ref={ref} style={style} className="max-w-3xl mx-auto text-center">
        <h2 className="text-[34px] md:text-[46px] font-semibold text-slate-900 tracking-tight leading-[1.05]" data-testid="story-mission-heading">
          Our{" "}
          <span className="relative inline-block">
            <span className="relative z-10 text-[#1d4ed8]">Mission</span>
            <span className="absolute left-0 right-0 bottom-1 h-3 bg-[#dbe6ff]/70 -z-0 rounded-sm" />
          </span>
        </h2>
        <p className="mt-5 text-slate-500 text-[16.5px] leading-relaxed">
          We believe every aspiring medic deserves access to high-quality interview preparation that
          is clear, affordable and always up to date. Our mission is to simplify the preparation
          journey by combining expert-written resources, the latest AI technology and realistic
          interview practice into one platform that gives every student the opportunity to prepare
          with confidence.
        </p>
      </div>
      <div className="mt-12 grid md:grid-cols-3 gap-6 md:gap-7">
        {MISSION_CARDS.map((c, i) => (
          <MissionCard key={c.id} item={c} i={i} />
        ))}
      </div>
    </section>
  );
};

/* ============ Pastel Illustration Gallery ============ */
const GALLERY_IMAGES = [
  { src: "/story/gallery-1.webp", alt: "Two medics discussing an anatomical heart model" },
  { src: "/story/gallery-5.png",  alt: "Doctor in consultation with a patient" },
  { src: "/story/gallery-4.png",  alt: "Pharmacist advising an elderly patient" },
  { src: "/story/gallery-2.png",  alt: "Three medics revising key topics" },
  { src: "/story/gallery-3.png",  alt: "Team practising MMI stations around a manikin" },
];

const GalleryTile = ({ item, i, sizeClass }) => {
  const { ref, inView } = useReveal();
  const style = {
    transform: inView ? "translate3d(0,0,0) scale(1)" : "translate3d(0, 22px, 0) scale(0.98)",
    opacity: inView ? 1 : 0,
    transition: `transform 900ms cubic-bezier(0.22, 1, 0.36, 1) ${i * 100}ms, opacity 700ms cubic-bezier(0.22, 1, 0.36, 1) ${i * 100}ms`,
    willChange: "transform, opacity",
  };
  return (
    <div
      ref={ref}
      style={style}
      className={`group relative rounded-[22px] overflow-hidden border border-white bg-white shadow-[0_25px_55px_-25px_rgba(29,78,216,0.35)] hover:shadow-[0_40px_80px_-25px_rgba(29,78,216,0.55)] hover:-translate-y-1 hover:scale-[1.015] transition-all duration-500 ${sizeClass}`}
      data-testid={`gallery-tile-${i}`}
    >
      {item?.src ? (
        <img
          src={item.src}
          alt={item.alt || ""}
          className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-[1.05]"
          draggable="false"
        />
      ) : (
        <div className="w-full h-full flex items-center justify-center min-h-[160px] bg-gradient-to-br from-[#eef4ff] via-white to-[#f7faff]">
          <span className="text-[11px] uppercase tracking-[0.16em] text-[#1d4ed8]/60 font-medium">
            Awaiting artwork
          </span>
        </div>
      )}
    </div>
  );
};

const PastelGallery = () => {
  /**
   * 5-tile masonry that resolves into a near-square composition on desktop.
   *
   *   +--------------------------+
   *   |            1             |   very wide banner (col-span-6)
   *   +--------------+-----------+
   *   |      2       |     3     |   16:9  +  4:3
   *   +--------------+-----------+
   *   |     4        |     5     |   wide  +  wide
   *   +--------------+-----------+
   */
  const layout = [
    "col-span-6 aspect-[24/7]",  // 1 — hero banner
    "col-span-4 aspect-[16/9]",  // 2 — big landscape
    "col-span-2 aspect-[4/5]",   // 3 — portrait-ish 4:3 upright
    "col-span-3 aspect-[16/9]",  // 4 — half-width landscape
    "col-span-3 aspect-[16/9]",  // 5 — half-width landscape
  ];
  const tiles = GALLERY_IMAGES.slice(0, layout.length);
  return (
    <section className="py-16 md:py-24" data-testid="story-gallery-section">
      <div className="max-w-6xl mx-auto px-6">
        <div className="mx-auto max-w-[960px]">
          <div className="grid grid-cols-6 gap-4 md:gap-6">
            {tiles.map((t, i) => (
              <GalleryTile key={i} item={t} i={i} sizeClass={layout[i]} />
            ))}
          </div>
        </div>
      </div>
    </section>
  );
};

/* ============ Values ============ */
const VALUES = [
  {
    id: "clarity",
    icon: Compass,
    title: "Clarity",
    body: "Helping students focus on what truly matters instead of information overload.",
  },
  {
    id: "accessibility",
    icon: Globe2,
    title: "Accessibility",
    body:
      "Making high-quality MMI preparation available to more aspiring medics through simple, transparent and affordable preparation.",
  },
  {
    id: "innovation",
    icon: Lightbulb,
    title: "Innovation",
    body:
      "Using the latest technology, including AI-powered practice, to help students prepare more effectively.",
  },
  {
    id: "quality",
    icon: BadgeCheck,
    title: "Quality",
    body: "Creating carefully written, continually updated resources that students can trust.",
  },
];

const ValueCard = ({ item, i }) => {
  const { ref, inView } = useReveal();
  const Icon = item.icon;
  const style = {
    transform: inView ? "translate3d(0,0,0)" : "translate3d(0, 22px, 0)",
    opacity: inView ? 1 : 0,
    transition: `transform 900ms cubic-bezier(0.22, 1, 0.36, 1) ${i * 110}ms, opacity 700ms cubic-bezier(0.22, 1, 0.36, 1) ${i * 110}ms`,
    willChange: "transform, opacity",
  };
  return (
    <div
      ref={ref}
      style={style}
      className="rounded-3xl bg-white border border-slate-100 p-6 md:p-7 shadow-[0_20px_50px_-25px_rgba(29,78,216,0.35)] hover:shadow-[0_30px_70px_-25px_rgba(29,78,216,0.5)] hover:-translate-y-1 transition-all duration-500"
      data-testid={`value-card-${item.id}`}
    >
      <span className="inline-flex w-11 h-11 rounded-2xl bg-[#eaf1ff] text-[#1d4ed8] items-center justify-center">
        <Icon className="w-[18px] h-[18px]" />
      </span>
      <h4 className="mt-5 text-[18px] font-semibold text-slate-900 tracking-tight">{item.title}</h4>
      <p className="mt-2 text-[14px] text-slate-600 leading-relaxed">{item.body}</p>
    </div>
  );
};

const Values = () => (
  <section className="max-w-6xl mx-auto px-6 pt-8 pb-24" data-testid="story-values-section">
    <div className="text-center max-w-2xl mx-auto">
      <span className="inline-block px-3 py-1 rounded-full bg-[#eef4ff] text-[#1d4ed8] text-[12px] font-medium">
        Our Values
      </span>
      <h2 className="mt-4 text-[34px] md:text-[46px] font-semibold text-slate-900 tracking-tight">
        What we{" "}
        <span className="relative inline-block">
          <span className="relative z-10 text-[#1d4ed8]">stand for</span>
          <span className="absolute left-0 right-0 bottom-1 h-3 bg-[#dbe6ff]/70 -z-0 rounded-sm" />
        </span>
      </h2>
    </div>
    <div className="mt-12 grid sm:grid-cols-2 lg:grid-cols-4 gap-5">
      {VALUES.map((v, i) => (
        <ValueCard key={v.id} item={v} i={i} />
      ))}
    </div>
  </section>
);

/* ============ Final CTA ============ */
const FinalCta = () => {
  const navigate = useNavigate();
  return (
    <section className="max-w-7xl mx-auto px-6 pb-24" data-testid="story-final-cta-section">
      <div className="rounded-[32px] bg-gradient-to-br from-[#eef4ff] via-white to-[#dbe6ff] border border-white p-12 md:p-20 text-center shadow-[0_45px_110px_-30px_rgba(29,78,216,0.55)]">
        <h3 className="text-[34px] md:text-[52px] font-semibold text-slate-900 tracking-tight leading-[1.05]">
          Your{" "}
          <span className="relative inline-block">
            <span className="relative z-10 text-[#1d4ed8]">journey</span>
            <span className="absolute left-0 right-0 bottom-1 h-3 bg-[#dbe6ff]/70 -z-0 rounded-sm" />
          </span>{" "}
          starts here.
        </h3>
        <p className="mt-5 text-slate-600 max-w-2xl mx-auto text-[16.5px] leading-relaxed">
          Whether you’re just beginning your preparation or refining your interview technique, we’re
          here to help you approach your MMI with confidence.
        </p>
        <div className="mt-9 flex justify-center">
          <button
            onClick={() => navigate("/pricing")}
            className="px-8 py-4 rounded-full bg-[#1d4ed8] text-white font-medium hover:bg-[#1e40af] transition shadow-[0_18px_45px_-12px_rgba(29,78,216,0.6)] inline-flex items-center gap-2"
            data-testid="story-cta-btn"
          >
            Start Preparing <ArrowRight className="w-4 h-4" />
          </button>
        </div>
      </div>
    </section>
  );
};

/* ============ Page ============ */
const OurStory = () => (
  <div className="bg-white min-h-screen">
    <Navbar />
    <Hero />
    <InteractiveLetter />
    <Mission />
    <PastelGallery />
    <Values />
    <FinalCta />
    <Footer />
  </div>
);

export default OurStory;
